// 举手（请求支援）发起抽屉：演示版，提交只写本地 mock，不连后端。
// 三个层级共用一套表单，类型/期望动作按层级切换；上下文由调用方传入（坐席带当前通话，组长/值班长带快照）。
// 坐席发起时可选择发给「组长」还是「班长（值班长）」；组长/值班长的目标固定（上送值班长 / 管理员）。

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Input,
  Message,
  Radio,
  Select,
  Space,
  Tag,
} from '@arco-design/web-react';
import { DRAWER_WIDTH } from '../../components';
import { useAuthStore } from '../../store/auth';
import { useCtxStore } from '../workbench/context/store';
import {
  SUPPORT_EXPECTS,
  SUPPORT_TYPES,
  useSupportMock,
  type SupportLevel,
  type SupportTarget,
} from './mock';

const LEVEL_TITLE: Record<SupportLevel, string> = {
  agent: '请求支援',
  leader: '向值班长求助',
  duty: '上报管理员 / 技术值班',
};

const LEVEL_HINT: Record<SupportLevel, string> = {
  agent: '把当前通话/会话和诉求推给组长、值班长或技术值班，对方能回复、监听、接管或派人',
  leader: '带上本组排队与人力快照，向值班长要人、要授权或要决策',
  duty: '带全场快照上报，处理人可技术介入、审批授权或跨部门协调',
};

/** 坐席发起时可选的接收人：组长（本组）/ 值班长（全场）/ 技术值班 */
const TARGET_OPTIONS: { label: string; value: SupportTarget }[] = [
  { label: '组长', value: 'leader' },
  { label: '值班长', value: 'duty' },
  { label: '技术', value: 'tech' },
];

/** 提交成功后的回执文案：按所选接收人直呼其名，别让坐席猜「技术」发给了谁 */
const TARGET_TOAST: Record<SupportTarget, string> = {
  leader: '组长',
  duty: '值班长',
  tech: '技术值班',
};

export default function SupportRequestDrawer({
  visible,
  onClose,
  level,
  contextLines = [],
}: {
  visible: boolean;
  onClose: () => void;
  level: SupportLevel;
  contextLines?: string[];
}) {
  const user = useAuthStore((s) => s.user);
  const userName = user?.name || user?.username || '我';
  const callUUID = useCtxStore((s) => s.callUUID);
  const chatId = useCtxStore((s) => s.chatId);
  const add = useSupportMock((s) => s.add);
  const items = useSupportMock((s) => s.items);
  const cancel = useSupportMock((s) => s.cancel);

  const [typeLabel, setTypeLabel] = useState<string>(SUPPORT_TYPES[level][0]);
  const [subject, setSubject] = useState('');
  const [expect, setExpect] = useState<string[]>([]);
  const [target, setTarget] = useState<SupportTarget>('leader');
  const [customerWaiting, setCustomerWaiting] = useState(false);
  const [note, setNote] = useState('');

  // 打开/切换层级时重置表单（不做“上次残留”）
  useEffect(() => {
    if (!visible) return;
    setTypeLabel(SUPPORT_TYPES[level][0]);
    setSubject('');
    setExpect([]);
    setTarget('leader');
    setCustomerWaiting(level === 'agent' && !!callUUID);
    setNote('');
  }, [visible, level, callUUID]);

  const mine = useMemo(
    () => items.filter((i) => i.level === level && i.fromName === userName).slice(0, 4),
    [items, level, userName],
  );

  const context = useMemo(() => {
    const out = [...contextLines];
    if (level === 'agent') {
      out.unshift(
        callUUID
          ? `当前通话：${callUUID.slice(0, 8)}…（已带入，对方可直接监听/接管）`
          : chatId
            ? `当前在线会话 #${chatId}（已带入）`
            : '当前没有在途通话/会话：将作为一般求助发出',
      );
    }
    return out;
  }, [contextLines, level, callUUID, chatId]);

  const submit = () => {
    if (!subject.trim()) {
      Message.warning('请先写一句诉求（60 字以内）');
      return;
    }
    if (expect.length === 0) {
      Message.warning('请至少勾选一个期望动作');
      return;
    }
    const input = {
      level,
      fromName: userName,
      // 坐席按所选发送；组长固定上送值班长；值班长的上报不进入两张现场卡
      target: level === 'agent' ? target : level === 'leader' ? 'duty' : undefined,
      typeLabel,
      subject: subject.trim(),
      expect,
      customerWaiting,
      context: [...context, note.trim() ? `补充：${note.trim()}` : ''].filter(Boolean),
    };
    add(input);
    Message.success(
      level === 'agent' ? `已发给${TARGET_TOAST[target]}（演示数据，未连后端）` : '已发出求助（演示数据，未连后端）',
    );
    setSubject('');
    setExpect([]);
    setNote('');
  };

  return (
    <Drawer
      title={LEVEL_TITLE[level]}
      visible={visible}
      onCancel={onClose}
      width={DRAWER_WIDTH.narrow}
      footer={
        <Space>
          <Button onClick={onClose}>关闭</Button>
          <Button type="primary" onClick={submit}>
            发出求助
          </Button>
        </Space>
      }
    >
      <Alert type="info" style={{ marginBottom: 12 }} content={`${LEVEL_HINT[level]}。当前为演示数据，不会真的通知任何人。`} />

      <div className="support-form">
        <div className="support-field">
          <div className="support-label">求助类型</div>
          <Select
            value={typeLabel}
            onChange={(v) => setTypeLabel(String(v))}
            options={SUPPORT_TYPES[level].map((t) => ({ label: t, value: t }))}
          />
        </div>

        <div className="support-field">
          <div className="support-label">一句话诉求</div>
          <Input
            value={subject}
            onChange={setSubject}
            maxLength={60}
            showWordLimit
            placeholder="如：客户要求退 30 元，需要授权"
          />
        </div>

        <div className="support-field">
          <div className="support-label">期望动作（可多选）</div>
          <Checkbox.Group value={expect} onChange={(v) => setExpect(v as string[])}>
            <Space wrap size={[16, 8]}>
              {SUPPORT_EXPECTS[level].map((t) => (
                <Checkbox key={t} value={t}>
                  {t}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </div>

        <div className="support-field">
          <div className="support-label">客户状态</div>
          <Checkbox checked={customerWaiting} onChange={setCustomerWaiting}>
            客户还在等
          </Checkbox>
        </div>

        <div className="support-field">
          <div className="support-label">自动带入的上下文（不可编辑）</div>
          <div className="support-context">
            {context.length === 0 ? (
              <div className="support-context-empty">无（演示环境未取到实时上下文）</div>
            ) : (
              context.map((c) => (
                <div key={c} className="support-context-line">
                  {c}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="support-field">
          <div className="support-label">补充说明（选填）</div>
          <Input.TextArea
            value={note}
            onChange={setNote}
            maxLength={200}
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder="客户原话、已尝试动作、希望多久内响应等"
          />
        </div>

        {level === 'agent' ? (
          <div className="support-field">
            <div className="support-label">发给谁</div>
            <Radio.Group type="button" value={target} onChange={(v) => setTarget(v as SupportTarget)}>
              {TARGET_OPTIONS.map((t) => (
                <Radio key={t.value} value={t.value}>
                  {t.label}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        ) : null}
      </div>

      {mine.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>我发出的求助（演示）</div>
          {mine.map((m) => (
            <div key={m.id} className="support-mine-row">
              <span>
                <Tag size="small" color="arcoblue">
                  {m.typeLabel}
                </Tag>
                <span style={{ marginLeft: 6 }}>{m.subject}</span>
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Tag size="small" color={m.status === 'pending' ? 'orange' : m.status === 'resolved' ? 'green' : 'gold'}>
                  {m.status === 'pending' ? '待响应' : m.status === 'accepted' ? '处理中' : m.status === 'resolved' ? '已解决' : '已升级'}
                </Tag>
                {m.status === 'pending' ? (
                  <Button
                    size="mini"
                    type="text"
                    onClick={() => {
                      cancel(m.id);
                      Message.success('已撤回（演示）');
                    }}
                  >
                    撤回
                  </Button>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </Drawer>
  );
}
